import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { produce } from 'immer';

import { bankFeedApi } from 'domain/bankFeed/api';
import type { EndpointBody, EndpointResponse } from 'services/api';
import { handleErrorResponseWithToast, yieldSuccessNotification } from 'lib/toasts';
import { bankFeedKeys } from './keys';

type SummaryCache = EndpointResponse<typeof bankFeedApi.getTransactionsGridSummary>;
type ExportVars = { body: EndpointBody<typeof bankFeedApi.triggerTransactionsExport> };
type ExportContext = { previous: SummaryCache | undefined };

const exportProcessingToast = {
  id: 'company.bankFeed.transactions.export.toast.processing',
  defaultMessage: 'Export processing',
};

const exportErrorFallbackToast = {
  id: 'company.bankFeed.transactions.export.toast.errorFallback',
  defaultMessage: 'Export failed',
};

// Optimistic-update mutation for transaction export.
//
// Trigger flow: onMutate flips `summary.exportInProgress = true` so the toolbar
// button shows spinner/disabled immediately. The HTTP response carries only
// `export_id`; the actual completion arrives over WebSocket
// (TRANSACTION_EXPORT_COMPLETED) and is handled by `BankFeedRoute/sagas.ts`.
// `onSuccess` is intentionally a noop — clearing the spinner is the WS handler's
// job (via setQueryData flipping `exportInProgress` back to false). `onError`
// rolls the optimistic flag back and surfaces the BE message via the
// standard toast.
export const useTriggerBankFeedTransactionsExportMutation = () => {
  const qc = useQueryClient();
  return useMutation<
    EndpointResponse<typeof bankFeedApi.triggerTransactionsExport>,
    AxiosError,
    ExportVars,
    ExportContext
  >({
    mutationFn: bankFeedApi.triggerTransactionsExport,
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: bankFeedKeys.transactionsSummary() });
      const previous = qc.getQueryData<SummaryCache>(bankFeedKeys.transactionsSummary());
      qc.setQueryData<SummaryCache>(
        bankFeedKeys.transactionsSummary(),
        produce((draft) => {
          if (!draft) return;
          draft.exportInProgress = true;
        }),
      );
      yieldSuccessNotification(exportProcessingToast);
      return { previous };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) {
        qc.setQueryData<SummaryCache>(bankFeedKeys.transactionsSummary(), ctx.previous);
      }
      handleErrorResponseWithToast(err, { defaultMessage: exportErrorFallbackToast });
    },
  });
};
